import React from 'react';
import { FormattedNumber } from 'react-intl';
import { fetchGet } from '../store/fetch';
import {
    CollapsiblePanel,
    Content,
    Panel,
    PanelBody,
    Table,
    TableBody,
    TableData,
    TableHead,
    TableHeader,
    TableHeadRow,
    TableRow,
    Tray,
} from './elements';
import { Spinner } from './spinners';
import { useReactAsync } from './utils/useReactAsync';

interface IDeal {
    id: number;
    portalId: number;
    name: string;
    closeDate: number;
    executionDate: number;
    executionDateYear: number;
    executionDateMonth: number;
    executionDateDay: number;
    amount: number;
    owner: string;
    stage: string;
}

interface IDealOverview {
    yearTotalAmount: number;
    yearTotalBudget: number;
    dealsNoExecutionDate: IDeal[];
    dealsExecutionDate: {
        year: string;
        month: string;
        totalAmount: number;
        deals: IDeal[];
        budget: number;
    }[];
}

async function loadDeals() {
    const url = '/api/hubSpotDeals';
    const result = await fetchGet(url);
    return result.json as IDealOverview;
}

// eslint-disable-next-line @typescript-eslint/require-await

export function HubSpotDealList() {
    const { data, isLoading } = useReactAsync(loadDeals, {}, []);

    const yearsToView = ['2023', '2024'];
    const currentYear = new Date().getFullYear();

    return (
        <>
            <Content>
                <Tray>
                    <Panel>
                        {(isLoading && <Spinner />) || (
                            <>
                                {(data && data?.dealsExecutionDate.length > 0 && (
                                    <>
                                        {yearsToView.map((itemYear, i) => (
                                            <>
                                                <CollapsiblePanel
                                                    heading={{ dark: true, text: itemYear, initialIsClosed: itemYear !== currentYear.toString() }}
                                                >
                                                    <p className="mt10 p10 h2">{itemYear}</p>
                                                    <PanelBody noPadding={true} key={i}>
                                                        <Table>
                                                            <TableHead>
                                                                <TableHeadRow light={false}>
                                                                    <TableHeader>Date</TableHeader>
                                                                    <TableHeader>Amount</TableHeader>
                                                                    <TableHeader>Name</TableHeader>
                                                                    <TableHeader>Stage</TableHeader>
                                                                </TableHeadRow>
                                                            </TableHead>
                                                            <TableBody>
                                                                {data &&
                                                                    data.dealsExecutionDate
                                                                        .filter((item) => item.year === itemYear)
                                                                        .map((item, key) => (
                                                                            <>
                                                                                <TableRow key={key} isRowSelected={true}>
                                                                                    <TableData nowrap>
                                                                                        <strong>
                                                                                            {item.year}-{item.month}
                                                                                        </strong>
                                                                                    </TableData>
                                                                                    <TableData nowrap>
                                                                                        {(item.totalAmount > item.budget && (
                                                                                            <span className="fw600 text-success fs15">
                                                                                                &euro;
                                                                                                <FormattedNumber value={item.totalAmount} />
                                                                                            </span>
                                                                                        )) || (
                                                                                            <span className="fw600 text-danger fs15">
                                                                                                &euro;
                                                                                                <FormattedNumber value={item.totalAmount} />
                                                                                            </span>
                                                                                        )}
                                                                                    </TableData>
                                                                                    <TableData>
                                                                                        Budget: &euro;
                                                                                        <FormattedNumber value={item.budget} />
                                                                                    </TableData>
                                                                                    <TableData>&nbsp;</TableData>
                                                                                    <TableData>&nbsp;</TableData>
                                                                                </TableRow>
                                                                                {item.deals.map((deal) => (
                                                                                    <TableRow key={deal.id}>
                                                                                        <TableData></TableData>
                                                                                        <TableData nowrap>
                                                                                            &euro;
                                                                                            <FormattedNumber value={deal.amount} />
                                                                                        </TableData>
                                                                                        <TableData>
                                                                                            <a
                                                                                                href={`https://app.hubspot.com/contacts/${deal.portalId}/deal/${deal.id}/`}
                                                                                                target="_blank"
                                                                                                rel="noreferrer"
                                                                                            >
                                                                                                {deal.name}
                                                                                            </a>
                                                                                        </TableData>
                                                                                        <TableData>{deal.stage}</TableData>
                                                                                    </TableRow>
                                                                                ))}
                                                                            </>
                                                                        ))}
                                                                {itemYear === currentYear.toString() && (
                                                                    <TableRow key="total" isRowSelected={true}>
                                                                        <TableData nowrap>
                                                                            <strong>Total:</strong>
                                                                        </TableData>
                                                                        <TableData nowrap>
                                                                            {(data.yearTotalAmount > data.yearTotalBudget && (
                                                                                <span className="fw600 text-success fs15">
                                                                                    &euro;
                                                                                    <FormattedNumber value={data.yearTotalAmount} />
                                                                                </span>
                                                                            )) || (
                                                                                <span className="fw600 text-danger fs15">
                                                                                    &euro;
                                                                                    <FormattedNumber value={data.yearTotalAmount} />
                                                                                </span>
                                                                            )}
                                                                        </TableData>
                                                                        <TableData>
                                                                            Budget: &euro;
                                                                            <FormattedNumber value={data.yearTotalBudget} />
                                                                        </TableData>
                                                                        <TableData>&nbsp;</TableData>
                                                                        <TableData>&nbsp;</TableData>
                                                                    </TableRow>
                                                                )}
                                                            </TableBody>
                                                        </Table>
                                                    </PanelBody>
                                                </CollapsiblePanel>
                                            </>
                                        ))}

                                        <CollapsiblePanel heading={{ dark: true, text: 'Items with no execution date', initialIsClosed: true }}>
                                            <PanelBody noPadding={true}>
                                                <p className="mt40 p10 h3">Items with no execution date</p>
                                                <Table>
                                                    <TableHead>
                                                        <TableHeadRow light={false}>
                                                            <TableHeader>Amount</TableHeader>
                                                            <TableHeader>Name</TableHeader>
                                                            <TableHeader>Stage</TableHeader>
                                                        </TableHeadRow>
                                                    </TableHead>
                                                    <TableBody>
                                                        {data &&
                                                            data.dealsNoExecutionDate.map((deal) => (
                                                                <>
                                                                    <TableRow key={deal.id}>
                                                                        <TableData nowrap>
                                                                            &euro;
                                                                            <FormattedNumber value={deal.amount} />
                                                                        </TableData>
                                                                        <TableData>
                                                                            <a
                                                                                href={`https://app.hubspot.com/contacts/${deal.portalId}/deal/${deal.id}/`}
                                                                                target="_blank"
                                                                                rel="noreferrer"
                                                                            >
                                                                                {deal.name}
                                                                            </a>
                                                                        </TableData>
                                                                        <TableData>{deal.stage}</TableData>
                                                                    </TableRow>
                                                                </>
                                                            ))}
                                                    </TableBody>
                                                </Table>
                                            </PanelBody>
                                        </CollapsiblePanel>
                                    </>
                                )) || <p>No Data</p>}
                            </>
                        )}
                    </Panel>
                </Tray>
            </Content>
        </>
    );
}
